import type { MapPin } from '../map/DispatchMap';
import { pinToneFor } from '../map/visitPinTone';
import type { Tone } from '../ui/Badge';
import { timeInTz } from '../../lib/format';

/**
 * The crew map's job pins, and the jobs it cannot draw (ops insights Phase 4, ticket 02).
 *
 * ⭐ Pure and beside `boardPins.ts` for the same reason: `apps/web` tests in node, so what
 * decides WHICH jobs appear lives where a test can hold it. The Map tab is wiring.
 *
 * ⚠️ **Not `dayPins`, and the difference is the day itself.** The board's day is the booked
 * day, so it orders by the minute of the shop's day. `crew.day`'s day is wider: a visit booked
 * yesterday and worked today is in it, and its minute of the day would sort it among today's
 * by yesterday's clock. So this orders by the booked INSTANT, then the id. And the reason a job
 * is not on the map is the server's (`unplacedWhy`, from the shared `whyUnplaced`), never a
 * second reading of a geocode status the read does not hand out.
 *
 * ⚠️ The visit fields read here are a subset of the read's allow-list (id, status, the booked
 * start): this module must never be the reason the server has to send more.
 */
export interface CrewJobSource {
  /** `scheduledStartAt` is nullable in the schema; the read's day never holds an unbooked one. */
  visit: { id: string; status: string; scheduledStartAt: Date | string | null };
  job: { title: string };
  customer: { displayName: string };
  property: { city: string | null };
  pin: { latitude: number; longitude: number } | null;
  /** The server's reason, present exactly when `pin` is null. */
  unplacedWhy?: string;
}

export interface CrewUnplaced {
  /** The VISIT id: what the pin, the row and the preview panel all key on. */
  id: string;
  /**
   * The number its pin would have carried, so the map's gap has an answer in the list. Null
   * while a tech is picked and this stop is not theirs (their pins carry no number either).
   */
  order: number | null;
  label: string;
  why: string;
}

/**
 * Said only if the server broke its promise (a null pin with no reason, or a coordinate that
 * is not a number). A job is never dropped for want of a sentence.
 */
export const NO_LOCATION = 'No location for this address';

/** An unbooked visit (never in the read's day) sorts last rather than throwing. */
const instant = (d: Date | string | null) =>
  d === null ? Number.POSITIVE_INFINITY : new Date(d).getTime();

const located = (pin: CrewJobSource['pin']): pin is NonNullable<CrewJobSource['pin']> =>
  pin !== null && Number.isFinite(pin.latitude) && Number.isFinite(pin.longitude);

/**
 * Today's jobs as pins, and the remainder named with its reason.
 *
 * ⚠️ **One pass, one partition**: every job is in exactly one of the two lists. A job in
 * neither is a visit that silently is not happening (`boardPins.ts` has the history).
 *
 * ⚠️ **Numbered over the whole day**, so an unplaced job leaves a gap on the map ("1, 2, 4")
 * and the list names job 3 under the same number.
 */
export function crewPins(
  jobs: readonly CrewJobSource[],
  tz: string,
): { pins: MapPin[]; unplaced: CrewUnplaced[] } {
  const ordered = [...jobs].sort(
    (a, b) =>
      instant(a.visit.scheduledStartAt) - instant(b.visit.scheduledStartAt) ||
      (a.visit.id < b.visit.id ? -1 : a.visit.id > b.visit.id ? 1 : 0),
  );
  const pins: MapPin[] = [];
  const unplaced: CrewUnplaced[] = [];
  ordered.forEach((j, i) => {
    const order = i + 1;
    if (located(j.pin)) {
      pins.push({
        id: j.visit.id,
        latitude: j.pin.latitude,
        longitude: j.pin.longitude,
        order,
        title: j.job.title,
        body: [
          j.visit.scheduledStartAt === null
            ? null
            : timeInTz(j.visit.scheduledStartAt, tz),
          j.customer.displayName,
          j.property.city,
        ]
          .filter(Boolean)
          .join(' · '),
        tone: pinToneFor(j.visit.status),
      });
    } else {
      unplaced.push({
        id: j.visit.id,
        order,
        label: [j.customer.displayName, j.job.title].filter(Boolean).join(' · '),
        why: j.unplacedWhy ?? NO_LOCATION,
      });
    }
  });
  return { pins, unplaced };
}

/* ───────────────────────── The people layer (ticket 03) ───────────────────────── */

/** Spec §2's five states, as `crew.day` names them (`crewPlacement.ts`). */
export type CrewState = 'on_job' | 'en_route' | 'between_jobs' | 'not_started' | 'done';

/** The state in words (CONTEXT.md's terms): every chip and face says it in text. */
export const CREW_STATE_LABEL: Record<CrewState, string> = {
  on_job: 'On a job',
  en_route: 'En route',
  between_jobs: 'Between jobs',
  not_started: 'Not started',
  done: 'Done for the day',
};

/**
 * The state's tone, in the app's one tone vocabulary (`ui/Badge`'s `Tone`, mapped to theme
 * colours once, by `badge--*` in theme.css): never a hex, never one per tech [Q3]. On a job
 * and en route share the brand, as the job pins share "active" for in progress and en route;
 * between jobs is info, not started neutral, done success. Colour is never the only carrier:
 * the state is in words.
 */
export const CREW_STATE_TONE: Record<CrewState, Tone> = {
  on_job: 'brand',
  en_route: 'brand',
  between_jobs: 'info',
  not_started: 'neutral',
  done: 'success',
};

/** One tech as `crew.day` answers it, as much as the map reads. */
export interface CrewTechSource {
  userId: string;
  name: string;
  initials: string;
  state: CrewState;
  place:
    | { kind: 'at'; visitId: string }
    | {
        kind: 'between';
        fromVisitId: string;
        toVisitId: string;
        leftAt: Date | string;
      }
    | null;
  /** The server's reason, when they are on a job or en route and `place` is null. */
  why?: string;
  /** Their visits in the day, in booked order: what a pick highlights and numbers. */
  visits: readonly string[];
  /** On a job: the visit they are at, whether or not the map can draw it (server, rule 1). */
  atVisitId: string | null;
  /** En route: the visit they are heading to first, drawn or not (server, rule 2, [Q1]). */
  toVisitId: string | null;
  /** Still booked, booked before their latest check-out: passed over (server, rule 3). */
  skipped: readonly string[];
}

/** "Maya" from "Maya Ortega": what a chip and a "<Name>'s day" button call someone. */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

/**
 * Said when the server placed a tech but the web cannot draw that place (a job missing from
 * the day, or an end with no pin). The server's own `why` wins when it sent one.
 */
export const NOT_DRAWN = 'Not on the map';

/** A face on the map: a tech at a job, or halfway along the line while en route. */
export interface CrewFace {
  userId: string;
  name: string;
  initials: string;
  state: CrewState;
  /** The point it belongs to: the job's pin, or the midpoint of the two job pins. */
  latitude: number;
  longitude: number;
  /**
   * En route: the dashed leg from the job they left to the job ahead, the only line the map
   * may slide their face along. Null: at a job's pin, which the map rings faces round.
   */
  leg: { from: [number, number]; to: [number, number] } | null;
  /** Under the transit face: "left A at T". */
  tag: string | null;
  /** The face's accessible name. */
  label: string;
  dimmed: boolean;
  picked: boolean;
}

/** One crew chip: every tech has exactly one, face or no face. */
export interface CrewChip {
  userId: string;
  name: string;
  firstName: string;
  initials: string;
  state: CrewState;
  stateLabel: string;
  why: string | null;
  /** Drawn as a face on the map; otherwise the chip is all there is of them. */
  onMap: boolean;
  picked: boolean;
}

/** The plain average of two coordinates: fine at city scale (spec §2). */
export function midpoint(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
): { latitude: number; longitude: number } {
  return {
    latitude: (a.latitude + b.latitude) / 2,
    longitude: (a.longitude + b.longitude) / 2,
  };
}

/**
 * The crew on the map: who gets a face and where (an en-route face carries its dashed leg),
 * and one chip per tech.
 *
 * ⚠️ **Every tech is in exactly one of `faces` or `chipsOnly`, and every tech has a chip.**
 * Between jobs, not started and done have no place, and a tech the map cannot draw keeps the
 * reason in their chip, so nobody silently disappears.
 *
 * 🔴 **Both ends, or no transit face.** An en-route face is drawn at the midpoint of the job
 * they left and the job they are heading to, and only when BOTH have a pin. With one end
 * missing there is no line to be halfway along, and a face at the one known end would claim a
 * position (ADR 0001): they stay a chip, with the reason.
 *
 * ⚠️ **Picking a tech** dims every other face with its leg (and `pickPins` their pins), and marks
 * theirs. A pick naming nobody on the roster (they left it since) dims nothing.
 */
export function crewPeople(
  techs: readonly CrewTechSource[],
  jobs: readonly CrewJobSource[],
  tz: string,
  picked: string | null,
): { faces: CrewFace[]; chips: CrewChip[]; chipsOnly: CrewChip[] } {
  const byVisit = new Map(jobs.map((j) => [j.visit.id, j]));
  const pinOf = (visitId: string) => {
    const pin = byVisit.get(visitId)?.pin ?? null;
    return located(pin) ? pin : null;
  };
  const named = (visitId: string) => byVisit.get(visitId)?.customer.displayName ?? '';
  const pick = techs.some((t) => t.userId === picked) ? picked : null;
  const dim = (userId: string) => pick !== null && userId !== pick;

  const faces: CrewFace[] = [];
  const chips: CrewChip[] = [];
  for (const t of techs) {
    const base = {
      userId: t.userId,
      name: t.name,
      initials: t.initials,
      state: t.state,
      dimmed: dim(t.userId),
      picked: t.userId === pick,
    };
    let drawn = false;
    const place = t.place;
    if (place?.kind === 'at') {
      const at = pinOf(place.visitId);
      if (at) {
        faces.push({
          ...base,
          latitude: at.latitude,
          longitude: at.longitude,
          leg: null,
          tag: null,
          label: `${t.name}, on a job at ${named(place.visitId)}`,
        });
        drawn = true;
      }
    } else if (place?.kind === 'between') {
      const a = pinOf(place.fromVisitId);
      const b = pinOf(place.toVisitId);
      if (a && b) {
        const left = timeInTz(place.leftAt, tz);
        const from = named(place.fromVisitId);
        faces.push({
          ...base,
          ...midpoint(a, b),
          leg: { from: [a.latitude, a.longitude], to: [b.latitude, b.longitude] },
          tag: `left ${from} at ${left}`,
          label: `${t.name}, en route from ${from} to ${named(place.toVisitId)}, left at ${left}`,
        });
        drawn = true;
      }
    }
    chips.push({
      userId: t.userId,
      name: t.name,
      firstName: firstName(t.name),
      initials: t.initials,
      state: t.state,
      stateLabel: CREW_STATE_LABEL[t.state] ?? t.state,
      why: t.why ?? (place !== null && !drawn ? NOT_DRAWN : null),
      onMap: drawn,
      picked: base.picked,
    });
  }
  return {
    faces,
    chips,
    chipsOnly: chips.filter((c) => !c.onMap),
  };
}

/**
 * The job pins with a pick applied (ticket 04): the picked tech's own visits stay bright and
 * carry THEIR day's numbers (1, 2, 3 in the order of their day, as the panel's timeline counts
 * them); every other pin is dimmed and carries NO number, so no visit on screen has two
 * numbers and no two pins share one. With nobody picked (or a pick naming nobody) the SAME
 * array comes back, so the map is handed nothing new.
 *
 * ⚠️ A pin is still the same pin (same id, same place), so the map re-icons it in place and
 * never refits for a pick.
 */
export function pickPins(
  pins: MapPin[],
  techs: readonly CrewTechSource[],
  picked: string | null,
  canceled: ReadonlySet<string>,
): MapPin[] {
  const tech = techs.find((t) => t.userId === picked);
  if (!tech) return pins;
  const order = dayOrder(tech, canceled);
  const theirs = new Set(tech.visits);
  return pins.map((p) =>
    theirs.has(p.id)
      ? { ...p, order: order.get(p.id) ?? null, dimmed: false }
      : { ...p, order: null, dimmed: true },
  );
}

/**
 * The not-on-the-map list with a pick applied, numbered as the pins are: the picked tech's
 * stops by THEIR day, everyone else's with no number (the gap in their numbering still has
 * its answer here). Nobody picked: the same list back.
 */
export function pickUnplaced(
  unplaced: CrewUnplaced[],
  techs: readonly CrewTechSource[],
  picked: string | null,
  canceled: ReadonlySet<string>,
): CrewUnplaced[] {
  const tech = techs.find((t) => t.userId === picked);
  if (!tech) return unplaced;
  const order = dayOrder(tech, canceled);
  return unplaced.map((u) => ({ ...u, order: order.get(u.id) ?? null }));
}

/**
 * visitId → its stop number in the tech's own day, from 1, in the order `crew.day` lists them.
 *
 * ⚠️ A canceled visit takes NO number: it is not a stop. Numbered, the day read "stop 1" for a
 * visit nobody will go to, and the first real stop became 2.
 */
export function dayOrder(
  tech: CrewTechSource,
  canceled: ReadonlySet<string>,
): Map<string, number> {
  const order = new Map<string, number>();
  for (const id of tech.visits) if (!canceled.has(id)) order.set(id, order.size + 1);
  return order;
}

/** The day's canceled visits, by id: what `dayOrder` leaves unnumbered. */
export function canceledVisits(jobs: readonly CrewJobSource[]): ReadonlySet<string> {
  return new Set(
    jobs.filter((j) => j.visit.status === 'canceled').map((j) => j.visit.id),
  );
}
